import type {
  LaunchItem,
  ResolvedActivityBarItem,
  WhenContext,
} from '@hibro/shared';
import { evaluateWhen, launchItemKey } from '@hibro/shared';

/**
 * Drop the launchers whose manifest `when` clause does not hold (见 03 §5.9 / 21)
 * — e.g. org-console showing its console entry only in an enterprise space.
 * The same result feeds the rail, `/open` rows, unknown-command suggestions,
 * and the `workbench.open` target table: `when` is existence, not a pin flag.
 *
 * The clause lives on the **contribution**, not on the projected
 * {@link LaunchItem}: visibility is re-decided per window against that window's
 * live `when` context, while the projection is the cross-window record of what
 * exists and where it sits. Rejoined here by composite key.
 *
 * Only `module` items are filtered. Built-ins and app links have no manifest to
 * carry a clause, and silently dropping them would take the settings gear with
 * them.
 *
 * **Fail-closed**: a clause naming a key nobody published evaluates false, so the
 * item hides. That is deliberate — the alternative (show it) would render against
 * state the platform could not read.
 */
export function filterLaunchItemsByWhen(
  items: readonly LaunchItem[],
  activityBar: readonly ResolvedActivityBarItem[],
  context: WhenContext,
): LaunchItem[] {
  const whenByKey = new Map(
    activityBar.map((item) => [
      launchItemKey(item.moduleId, item.id),
      item.when,
    ]),
  );
  return items.filter((item) => {
    if (item.source !== 'module') {
      return true;
    }
    return evaluateWhen(whenByKey.get(item.key), context);
  });
}
